/* eslint-disable react-refresh/only-export-components -- Hooks must be exported with Provider */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { FeatureFlags } from '@/types/featureFlags';
import { DEFAULT_FLAGS } from '@/types/featureFlags';

const API_BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:8000') + '/api/v2';

interface FeatureFlagContextType {
  flags: FeatureFlags;
  isLoading: boolean;
  /** True once flags have been fetched at least once (success or error). */
  hasLoaded: boolean;
  error: Error | null;
  isEnabled: (key: string) => boolean;
  refetch: () => Promise<void>;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined);

interface FeatureFlagProviderProps {
  children: ReactNode;
}

export function FeatureFlagProvider({ children }: FeatureFlagProviderProps) {
  const [flags, setFlags] = useState<FeatureFlags>(DEFAULT_FLAGS);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchFlags = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/flags`, { signal });

      if (!response.ok) {
        throw new Error(`Failed to fetch feature flags: ${response.status}`);
      }

      const data = await response.json();
      setFlags(data);
      setHasLoaded(true);
      setIsLoading(false);
    } catch (err) {
      // Ignore aborted requests (e.g. React StrictMode cleanup).
      // Do NOT update state here - the component is unmounting and the
      // second mount will start its own fetch.  Setting isLoading=false
      // while flags are still DEFAULT_FLAGS causes premature redirects.
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      const errorObj = err instanceof Error ? err : new Error('Unknown error fetching flags');
      setError(errorObj);
      console.warn('Failed to fetch feature flags, using defaults:', errorObj);
      // Keep DEFAULT_FLAGS on error
      setFlags(DEFAULT_FLAGS);
      setHasLoaded(true);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchFlags(controller.signal);
    return () => controller.abort();
  }, [fetchFlags]);

  const isEnabled = useCallback(
    (key: string): boolean => {
      return flags[key] ?? false;
    },
    [flags]
  );

  const value: FeatureFlagContextType = {
    flags,
    isLoading,
    hasLoaded,
    error,
    isEnabled,
    refetch: fetchFlags,
  };

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}

/**
 * Hook to access the full feature flag context.
 *
 * @returns Full context with flags, loading state, error, and utility methods
 */
export function useFeatureFlags(): FeatureFlagContextType {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagProvider');
  }
  return context;
}

/**
 * Hook to check a single feature flag.
 *
 * @param key - The feature flag key to check
 * @returns Boolean indicating if the flag is enabled
 */
export function useFeatureFlag(key: string): boolean {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(key);
}

/**
 * Hook to check a single feature flag, returning false if no provider exists.
 * Use this in optional, non-critical components that may render outside
 * the FeatureFlagProvider (e.g. in test environments).
 *
 * @param key - The feature flag key to check
 * @returns Boolean indicating if the flag is enabled (false when no provider)
 */
export function useFeatureFlagSafe(key: string): boolean {
  const context = useContext(FeatureFlagContext);
  if (!context) return false;
  return context.isEnabled(key);
}

/**
 * Hook to get a feature flag with loading state.
 * Useful when you need to show loading UI while flags are being fetched.
 *
 * @param key - The feature flag key to check
 * @returns Object with enabled boolean and isLoading state
 */
export function useFeatureFlagWithLoading(key: string): {
  enabled: boolean;
  isLoading: boolean;
  hasLoaded: boolean;
} {
  const { isEnabled, isLoading, hasLoaded } = useFeatureFlags();
  return {
    enabled: isEnabled(key),
    isLoading,
    hasLoaded,
  };
}
